'use client'
import React from "react";
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { green, orange, grey } from "@mui/material/colors";
import { ILocationVisit, LOCATION_VISIT_STATUS } from "@/stack-shared/interfaces/model/location-visit.interface";

// Default status if missing
const DEFAULT_STATUS = LOCATION_VISIT_STATUS.NOT_STARTED;

// Function to return status icons
const getStatusIcon = (status: LOCATION_VISIT_STATUS | null | undefined) => {
  const resolvedStatus = status ?? DEFAULT_STATUS;

  switch (resolvedStatus) {
    case LOCATION_VISIT_STATUS.COMPLETED:
      return <CheckCircleIcon sx={{ color: green[500], fontSize: 18 }} />;
    case LOCATION_VISIT_STATUS.PENDING:
      return <HourglassEmptyIcon sx={{ color: orange[500], fontSize: 18 }} />;
    case LOCATION_VISIT_STATUS.NOT_STARTED:
    default:
      return <ScheduleIcon sx={{ color: grey[500], fontSize: 18 }} />;
  }
};

// Props interface
interface SiteVisitOverviewCardProps {
  siteVisit: ILocationVisit;
}

const SiteVisitOverviewCard: React.FC<SiteVisitOverviewCardProps> = ({ siteVisit }) => {
  if (!siteVisit) return null;

  const formattedDate = new Date(siteVisit.createdAt || new Date()).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <Card variant="outlined" sx={{ display: "flex", alignItems: "center", p: 1 }}>
      <CardContent sx={{ width: "100%", p: 1 }}>
        <Grid container spacing={1} alignItems="center">
          {/* Date and Main Status - Always Inline */}
          <Grid size={{sm: 6, xs: 12, md:'auto'}} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">
              <strong>{formattedDate}</strong>
            </Typography>
            {getStatusIcon(siteVisit.status)}
            <Typography variant="body2">{(siteVisit.status ?? DEFAULT_STATUS).replace("_", " ")}</Typography>
          </Grid>

          {/* Individual Statuses - Inline on Large, Stacked on Small */}
          <Grid size={{sm: 6, xs: 12, md:'auto'}} >
            <Grid container spacing={0.5} sx={{ justifyContent: { xs: "flex-start", md: "center" } }}>
              {[
                { label: "G", status: siteVisit.goaStatus },
                { label: "T", status: siteVisit.trkStatus },
                { label: "C", status: siteVisit.ctmStatus },
                { label: "A", status: siteVisit.atmStatus },
              ].map(({ label, status }) => (
                <Grid
                  size={{ xs: 3, md:'auto'}}
                  key={label}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {getStatusIcon(status)}
                  <Typography variant="caption">{label}</Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SiteVisitOverviewCard;
